import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { IconFileText, IconSearch } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import type { Questionnaire } from '@medplum/fhirtypes';
import { useResolvedTemplates } from '../../settings/template-library/hooks';
import {
  filterTreatmentPlanningDocumentTemplates,
  getChartDocumentTemplateDisplay,
} from '../../../lib/chart/chartDocumentTemplates';

export interface AddOtherDocumentModalProps {
  opened: boolean;
  onClose: () => void;
  onSelectTemplate: (template: Questionnaire) => void;
}

export function AddOtherDocumentModal({
  opened,
  onClose,
  onSelectTemplate,
}: AddOtherDocumentModalProps): JSX.Element {
  const [search, setSearch] = useState('');
  const { templates, loading, error } = useResolvedTemplates({
    target: 'chart-document',
    category: 'treatment-planning',
    skip: !opened,
  });

  const chartTemplates = useMemo(
    () => filterTreatmentPlanningDocumentTemplates(templates),
    [templates],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return chartTemplates;
    return chartTemplates.filter((t) => {
      const display = getChartDocumentTemplateDisplay(t);
      return (
        display.title.toLowerCase().includes(q)
        || (display.categoryLabel?.toLowerCase().includes(q) ?? false)
        || (display.description?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [chartTemplates, search]);

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add other document"
      size="lg"
      centered
    >
      <Stack gap="md">
        <Text size="sm" c="gray.7">
          Select a treatment planning template to add to this client&apos;s chart.
        </Text>
        <TextInput
          placeholder="Search templates..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        {loading && (
          <Group justify="center" py="xl">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">Loading templates…</Text>
          </Group>
        )}

        {!loading && error && (
          <Alert color="red" variant="light" title="Could not load templates">
            {error}
          </Alert>
        )}

        {!loading && !error && filtered.length === 0 && (
          <Text size="sm" c="gray.6" ta="center" py="lg">
            {chartTemplates.length === 0
              ? 'No treatment planning templates are available. Add active templates under Settings → Documentation.'
              : 'No templates match your search.'}
          </Text>
        )}

        {!loading && !error && filtered.length > 0 && (
          <ScrollArea.Autosize mah={360} type="auto">
            <Stack gap="xs">
              {filtered.map((template) => {
                const display = getChartDocumentTemplateDisplay(template);
                return (
                  <UnstyledButton
                    key={display.id}
                    onClick={() => {
                      onSelectTemplate(template);
                      handleClose();
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: 'var(--mantine-spacing-md)',
                      borderRadius: 'var(--mantine-radius-md)',
                      border: '1px solid var(--mantine-color-gray-3)',
                      transition: 'background-color 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--mantine-color-gold-0)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '';
                    }}
                  >
                    <Group wrap="nowrap" align="flex-start" gap="sm">
                      <IconFileText size={20} style={{ flexShrink: 0, marginTop: 2, color: 'var(--mantine-color-gray-6)' }} />
                      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                        <Group gap="xs" wrap="wrap">
                          <Text fw={600} size="sm" style={{ flex: 1, minWidth: 0 }}>
                            {display.title}
                          </Text>
                          {display.categoryLabel && (
                            <Badge variant="light" size="sm" color="blue">
                              {display.categoryLabel}
                            </Badge>
                          )}
                        </Group>
                        {display.description && (
                          <Text size="xs" c="gray.6" lineClamp={2}>
                            {display.description}
                          </Text>
                        )}
                      </Stack>
                    </Group>
                  </UnstyledButton>
                );
              })}
            </Stack>
          </ScrollArea.Autosize>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose}>Cancel</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
